
import { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import LoginForm from "@/components/auth/LoginForm";
import SignupForm from "@/components/auth/SignupForm";
import { useAuth } from "@/contexts/auth";
import { ForgotPasswordForm } from "@/components/auth/ForgotPasswordForm";
import { ResetPasswordForm } from "@/components/auth/ResetPasswordForm";
import { Button } from "@/components/ui/button";
import Layout from "@/components/Layout";
import { AuthModeHandler } from "@/components/auth/AuthModeHandler";
import { AuthRedirectHandler } from "@/components/auth/AuthRedirectHandler";
import { AuthModeEffect } from "@/components/auth/AuthModeEffect";
import { debugLogger } from '@/utils/debug';
import { sanitizeRedirectPath } from '@/utils/auth/sanitizeRedirect';
import { useTranslation } from "react-i18next";
import useFeatureFlag from "@/hooks/useFeatureFlag";
import MfaChallengeForm from "@/components/auth/MfaChallengeForm";
import { useUserProfile } from "@/contexts/profile/ProfileContext";

const authTabListClassName = "mb-6 grid h-auto w-full grid-cols-2 rounded-md border border-brand-cloud bg-white p-1 shadow-sm";
const authTabTriggerClassName = "text-sm font-medium data-[state=active]:bg-brand-mist data-[state=active]:text-brand-ink data-[state=active]:shadow-none";

const AuthPage = () => {
  const { t, i18n } = useTranslation('auth');
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const redirectPath = sanitizeRedirectPath(searchParams.get("redirect"));
  const authError = searchParams.get("authError");
  // Get token from either 'token' or 'access_token' parameter
  const resetToken = searchParams.get("token") || searchParams.get("access_token") || "";
  const hasRedirectParam = searchParams.has("redirect");
  const tabParam = searchParams.get("tab") || "login"; // Default to login if not specified
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user, loading } = useAuth();
  const { profile, isLoading: isLoadingProfile } = useUserProfile();
  const enableWhatsAppMfa = useFeatureFlag("ENABLE_WHATSAPP_MFA");
  const shouldRequireMfaChallenge = Boolean(user && enableWhatsAppMfa && profile?.mfa_enabled);

  const authErrorMessage = useMemo(() => {
    switch (authError) {
      case 'missing_code':
        return 'Google sign-in did not return an authorization code. Please try again.';
      case 'exchange_failed':
        return 'Google sign-in could not be completed. Please try again.';
      case 'inactive_user':
        return 'Your account has been deactivated by an administrator. Please contact us for assistance.';
      default:
        return null;
    }
  }, [authError]);
  
  // Log URL parameters for debugging
  useEffect(() => {
    debugLogger.log("Auth page URL parameters:", { 
      tab: searchParams.get("tab"),
      mode: searchParams.get("mode"),
      type: searchParams.get("type"),
      redirect: redirectPath, 
      token: resetToken ? "exists" : "none",
      allParams: Object.fromEntries(searchParams.entries())
    });
  }, [searchParams, redirectPath, resetToken]);

  // Show a simple loading state while auth is initializing
  if (loading) {
    return (
      <Layout>
        <div className="flex min-h-screen items-center justify-center bg-brand-mist px-4 py-16">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" role="status" />
            <p className="mt-4">{t("loading")}</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (user && enableWhatsAppMfa && isLoadingProfile) {
    return (
      <Layout>
        <div className="flex min-h-screen items-center justify-center bg-brand-mist px-4 py-16">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" role="status" />
            <p className="mt-4">{t("loading")}</p>
          </div>
        </div>
      </Layout>
    );
  }

  // If user is authenticated and there's a redirect parameter, handle the redirect
  if (user) {
    if (shouldRequireMfaChallenge) {
      return (
        <Layout>
          <MfaChallengeForm redirectPath={redirectPath} />
        </Layout>
      );
    }

    return <AuthRedirectHandler />;
  }

  // Check if current language is not English
  const isNonEnglish = i18n.language !== 'en';

  // Show login/signup form if not authenticated
  return (
    <Layout>
      <section className="relative isolate flex min-h-screen items-center justify-center bg-dark-surface px-4 py-16">
        <div
          className="auth-page-backdrop absolute inset-0 -z-10 bg-cover bg-center"
        />
        <Card className="w-full max-w-md rounded-md border-brand-cloud bg-white shadow-lg">
          <AuthModeHandler>
            {(authMode, setAuthMode) => {
              return (
                <>
                  <AuthModeEffect tabParam={tabParam} setAuthMode={setAuthMode} />
                  <CardHeader className="pb-4">
                    <CardTitle className="text-center font-heading text-brand-ink">
                      {authMode === "login" && t("login.title")}
                      {authMode === "signup" && t("signup.title")}
                      {authMode === "forgotPassword" && t("forgotPassword.title")}
                      {authMode === "resetPassword" && t("resetPassword.title")}
                    </CardTitle>
                    <CardDescription className="text-center text-brand-slate">
                      {authMode === "login" && t("login.description")}
                      {authMode === "signup" && t("signup.description")}
                      {authMode === "forgotPassword" && t("forgotPassword.description")}
                      {authMode === "resetPassword" && t("resetPassword.description")}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {authErrorMessage && (
                      <div className="mb-4 rounded-md bg-red-50 p-3 text-center text-sm text-coral">
                        {authErrorMessage}
                      </div>
                    )}
                    {/* Language notice for non-English users on signup */}
                    {authMode === "signup" && isNonEnglish && (
                      <div className="mb-4 rounded-md border border-amber bg-amber-soft p-3">
                        <p className="text-sm text-brand-ink">
                          {t("languageNote")}
                        </p>
                      </div>
                    )}
                    
                    {(authMode === "login" || authMode === "signup") && (
                      <Tabs 
                        value={authMode} 
                        onValueChange={(value) => {
                          debugLogger.log("Tab changed to:", value);
                          setAuthMode(value as "login" | "signup");
                        }}
                      >
                        <TabsList className={authTabListClassName}>
                          <TabsTrigger className={authTabTriggerClassName} value="login">{t("login.title")}</TabsTrigger>
                          <TabsTrigger className={authTabTriggerClassName} value="signup">{t("signup.title")}</TabsTrigger>
                        </TabsList>
                        <TabsContent value="login">
                          <LoginForm
                            isSubmitting={isSubmitting}
                            setIsSubmitting={setIsSubmitting}
                            redirectPath={redirectPath}
                          />
                          <div className="mt-4 text-center">
                            <Button 
                              variant="link" 
                              className="p-0 h-auto text-sm"
                              onClick={() => setAuthMode("forgotPassword")}
                            >
                              {t("login.forgotPassword")}
                            </Button>
                          </div>
                        </TabsContent>
                        <TabsContent value="signup">
                          <SignupForm
                            isSubmitting={isSubmitting}
                            setIsSubmitting={setIsSubmitting}
                            setAuthMode={setAuthMode}
                            redirectPath={redirectPath}
                          />
                        </TabsContent>
                      </Tabs>
                    )}
                    
                    {authMode === "forgotPassword" && (
                      <ForgotPasswordForm 
                        onBack={() => setAuthMode("login")}
                        isSubmitting={isSubmitting}
                        setIsSubmitting={setIsSubmitting}
                      />
                    )}
                    
                    {authMode === "resetPassword" && (
                      <ResetPasswordForm
                        token={resetToken}
                        onBack={() => setAuthMode("login")}
                        isSubmitting={isSubmitting}
                        setIsSubmitting={setIsSubmitting}
                      />
                    )}
                  </CardContent>
                </>
              );
            }}
          </AuthModeHandler>
        </Card>
      </section>
    </Layout>
  );
};

export default AuthPage;
